import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { excuseApi } from "../../commons/http";

const ScheduledAlertForm = ({ onSubmit, initialData = {}, isEdit = false }) => {
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    scheduled_date: null,
    scheduled_hour: "",
    priority: "medium",
    user_id: null,
  });
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [errors, setErrors] = useState({});

  // Generate hour options (0-23)
  const hourOptions = Array.from({ length: 24 }, (_, i) => ({
    value: i,
    label: `${i.toString().padStart(2, "0")}:00`,
  }));

  useEffect(() => {
    // Fetch users for the autocomplete
    const fetchUsers = async () => {
      try {
        const res = await excuseApi({
          path: "/users",
          method: "GET",
          params: { per_page: 1000 },
        });
        if (res.status === 200) {
          setUsers(res.data.data || []);
        }
      } catch (error) {
        console.error("Failed to fetch users", error);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    if (initialData && initialData.id) {
      const scheduledAt = initialData.scheduled_at
        ? dayjs(initialData.scheduled_at)
        : null;

      setFormData({
        title: initialData.title || "",
        message: initialData.message || "",
        scheduled_date: scheduledAt,
        scheduled_hour: scheduledAt ? scheduledAt.hour() : "",
        priority: initialData.priority || "medium",
        user_id: initialData.user_id || null,
      });

      // Set selected user if user_id exists
      if (initialData.user_id && users.length > 0) {
        const user = users.find((u) => u.id === initialData.user_id);
        setSelectedUser(user || null);
      }
    }
  }, [initialData, users]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleDateChange = (newValue) => {
    setFormData((prevData) => ({
      ...prevData,
      scheduled_date: newValue,
    }));
    if (errors.scheduled_date) {
      setErrors((prev) => ({ ...prev, scheduled_date: null }));
    }
  };

  const handleUserChange = (event, newValue) => {
    setSelectedUser(newValue);
    setFormData((prevData) => ({
      ...prevData,
      user_id: newValue ? newValue.id : null,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Tiêu đề là bắt buộc";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Nội dung là bắt buộc";
    }

    if (!formData.scheduled_date) {
      newErrors.scheduled_date = "Ngày nhắc nhở là bắt buộc";
    }

    if (formData.scheduled_hour === "" || formData.scheduled_hour === null) {
      newErrors.scheduled_hour = "Giờ nhắc nhở là bắt buộc";
    }

    // Check if scheduled time is in the future
    if (formData.scheduled_date && formData.scheduled_hour !== "") {
      const scheduledAt = formData.scheduled_date
        .hour(formData.scheduled_hour)
        .minute(0)
        .second(0);

      if (scheduledAt.isBefore(dayjs())) {
        newErrors.scheduled_date = "Thời gian nhắc nhở phải trong tương lai";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Combine date and hour into ISO string
    const scheduledAt = formData.scheduled_date
      .hour(formData.scheduled_hour)
      .minute(0)
      .second(0)
      .toISOString();

    onSubmit({
      title: formData.title,
      message: formData.message,
      scheduled_at: scheduledAt,
      priority: formData.priority,
      user_id: formData.user_id,
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container component="main" maxWidth="sm">
        <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
          {isEdit ? "Chỉnh sửa nhắc nhở" : "Tạo nhắc nhở mới"}
        </Typography>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            margin="normal"
            required
            fullWidth
            id="title"
            label="Tiêu đề"
            name="title"
            value={formData.title}
            onChange={handleChange}
            error={!!errors.title}
            helperText={errors.title}
          />

          <TextField
            margin="normal"
            required
            fullWidth
            id="message"
            label="Nội dung"
            name="message"
            multiline
            rows={4}
            value={formData.message}
            onChange={handleChange}
            error={!!errors.message}
            helperText={errors.message}
          />

          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <DatePicker
              label="Ngày nhắc nhở"
              value={formData.scheduled_date}
              onChange={handleDateChange}
              minDate={dayjs()}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  error: !!errors.scheduled_date,
                  helperText: errors.scheduled_date,
                },
              }}
            />

            <FormControl fullWidth required error={!!errors.scheduled_hour}>
              <InputLabel>Giờ</InputLabel>
              <Select
                name="scheduled_hour"
                value={formData.scheduled_hour}
                label="Giờ"
                onChange={handleChange}
              >
                {hourOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.scheduled_hour && (
                <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                  {errors.scheduled_hour}
                </Typography>
              )}
            </FormControl>
          </Box>

          <FormControl fullWidth margin="normal">
            <InputLabel>Độ ưu tiên</InputLabel>
            <Select
              name="priority"
              value={formData.priority}
              label="Độ ưu tiên"
              onChange={handleChange}
            >
              <MenuItem value="low">Thấp</MenuItem>
              <MenuItem value="medium">Trung bình</MenuItem>
              <MenuItem value="high">Cao</MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            options={users}
            getOptionLabel={(option) => option.name || ""}
            value={selectedUser}
            onChange={handleUserChange}
            filterOptions={(options, { inputValue }) => {
              const filterValue = inputValue.toLowerCase();
              return options.filter(
                (option) =>
                  (option.name && option.name.toLowerCase().includes(filterValue)) ||
                  (option.phone_number && option.phone_number.includes(filterValue)) ||
                  (option.address && option.address.toLowerCase().includes(filterValue))
              );
            }}
            renderOption={(props, option) => {
              const { key, ...otherProps } = props;
              return (
                <Box
                  component="li"
                  key={key}
                  {...otherProps}
                  sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start !important" }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {option.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.phone_number || "Chưa có SĐT"}
                    {option.address && ` • ${option.address}`}
                  </Typography>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="normal"
                label="Khách hàng (Tùy chọn)"
                placeholder="Tìm theo tên, SĐT hoặc địa chỉ"
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
          >
            {isEdit ? "Cập nhật" : "Tạo mới"}
          </Button>
        </Box>
      </Container>
    </LocalizationProvider>
  );
};

export default ScheduledAlertForm;
